import type {TableColumnsType} from 'antd';
import {
  Alert,
  Button,
  Col,
  Image,
  Input,
  message,
  Modal,
  Row,
  Skeleton,
  Table,
  Tag,
} from 'antd';
import React, {useEffect, useState} from 'react';

import {CloseCircleOutlined, RollbackOutlined} from '@ant-design/icons';
import {useCancelOrderById, useGetOrderById} from 'hooks/order/useGetOrder';
import {useNavigate, useParams} from 'react-router-dom';
import {addKeyData, formatNumberToString} from 'utils';
import {
  ORDER_STATUS,
  ORDER_STATUS_COLOR,
  ORDER_STATUS_LABEL,
} from 'types/order';

const QUICK_CANCEL_REASONS = [
  'Khách hàng yêu cầu hủy',
  'Quán hết món / đóng cửa',
  'Không tìm thấy tài xế giao hàng',
  'Sai thông tin địa chỉ / số điện thoại',
  'Đơn hàng trùng lặp',
];

const paymentMethod: Record<string, string> = {
  Cash: 'Tiền mặt',
  Banking: 'Chuyển khoản',
};

const CANCELED_BY_LABEL: Record<string, string> = {
  client: 'Khách hàng',
  user: 'Khách hàng',
  ship: 'Tài xế',
  shipper: 'Tài xế',
  driver: 'Tài xế',
  shop: 'Quán ăn',
  admin: 'Quản trị viên',
  system: 'Hệ thống',
};

const getCanceledByText = (canceledBy?: string) => {
  if (!canceledBy) return '';
  const key = canceledBy.toLowerCase();
  return CANCELED_BY_LABEL[key] || canceledBy;
};

export const OrderByID: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [messageApi, contextHolder] = message.useMessage();

  const {mutateAsync, isLoading} = useCancelOrderById();
  const {
    data,
    refetch,
    isLoading: isLoadingData,
    isSuccess,
    isError,
  } = useGetOrderById(params?.id || '');
  useEffect(() => {
    if (isError) {
      message.error(`Không tìm thấy order có id: ${params?.id}`);
      setTimeout(() => {
        navigate('/orders', {
          state: {from: `/orders/${params?.id}`},
        });
      }, 2000);
    }
    if (isSuccess) {
      document.title = `Đơn hàng: # ${data?.code}`;
    }
  }, [isSuccess, data, isError]);
  const columns: TableColumnsType = [
    {title: 'Tên món ăn', dataIndex: 'name', key: 'name'},
    {
      title: 'Hình ảnh',
      dataIndex: 'image',
      key: 'image',
      render: data => {
        return (
          <Image
            width={100}
            height={100}
            src={data}
            fallback="data:image/png;base64,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"
          />
        );
      },
    },
    {
      title: 'Giá',
      dataIndex: 'price',
      key: 'price',
      render: data => {
        return (
          <div>
            <p>{formatNumberToString(data)}</p>
          </div>
        );
      },
    },
    {title: 'Số lượng', dataIndex: 'quantity', key: 'quantity'},
    {title: 'Loại món ăn', dataIndex: 'type', key: 'type'},
    {
      title: 'Kích thước',
      dataIndex: '',
      key: 'size',
      render: data => {
        if (!data?.selectedSize) return null;
        const key = Object.keys(data?.selectedSize)[0];
        const value = data?.selectedSize[key];
        return (
          <div className="flex flex-col gap-1">
            <p>
              {key}: {`+${formatNumberToString(value)} VNĐ`}
            </p>
          </div>
        );
      },
    },
    {
      title: 'Thành phần',
      dataIndex: '',
      key: 'ingredients',
      render: data => {
        const ingredients = formatMenu(data?.selectedIngredients || []);
        return (
          <div className="flex flex-col gap-1">
            {ingredients.map(item => {
              return (
                <div>
                  <p>
                    {item.label}: {item.value}{' '}
                  </p>
                </div>
              );
            })}
          </div>
        );
      },
    },
    {title: 'Note', dataIndex: 'note', key: 'note'},
  ];
  const formatMenu = (arr = []) => {
    const newArr = [];
    for (const item of arr) {
      const key = Object.keys(item)[0];
      const value = item[key];
      newArr.push({label: key, value: `+${formatNumberToString(value)} VNĐ`});
    }
    return newArr;
  };
  const sumTotal = (s1 = 0, s2 = 0, s3 = 0) => {
    const sum = Number(s1) + Number(s2) + Number(s3);
    return formatNumberToString(sum);
  };
  console.log(isLoadingData && isError, isLoadingData, isError);
  return (
    <div className="container mx-auto">
      {contextHolder}
      <Skeleton active loading={isLoadingData || isError}>
        <div className="flex-col gap-4">
          <div className="flex justify-between items-center">
            <div className="flex gap-2 items-center">
              <h2 className="text-2xl font-bold">Orders #{data?.code}</h2>
              <Tag color={ORDER_STATUS_COLOR[data?.status] || 'default'}>
                {ORDER_STATUS_LABEL[data?.status as ORDER_STATUS] || data?.status?.toLocaleUpperCase()}
              </Tag>
            </div>
            <div className="flex gap-2">
              <Button
                icon={<RollbackOutlined />}
                onClick={() => {
                  navigate(-1);
                }}>
                Trở về
              </Button>
              {data?.status === 'completed' ||
              data?.status === 'canceled' ? null : (
                <Button
                  icon={<CloseCircleOutlined />}
                  danger
                  loading={isLoading}
                  type="primary"
                  onClick={() => {
                    setReason('');
                    setIsOpen(true);
                  }}>
                  Huỷ đơn
                </Button>
              )}
            </div>
          </div>
          {/* Hiển thị lý do hủy đơn hàng nếu đơn bị hủy hoặc có cancelReason */}
          {(data?.cancelReason || data?.status === ORDER_STATUS.Canceled) && (
            <Alert
              className="my-3 border-l-4 border-red-500 shadow-sm"
              type="error"
              showIcon
              message={
                <span className="font-bold text-base text-red-700">
                  Đơn hàng đã bị hủy {data?.canceledBy ? `bởi ${getCanceledByText(data.canceledBy)}` : ''}
                </span>
              }
              description={
                <div className="mt-1 text-sm text-gray-800">
                  <span className="font-semibold text-red-600">Lý do hủy đơn: </span>
                  <span className="font-medium text-gray-900">{data?.cancelReason || 'Không có lý do cụ thể'}</span>
                </div>
              }
            />
          )}
          <div className=" flex flex-col gap-2">
            <Row gutter={[12, 12]}>
              <Col xs={24} md={12}>
                <label className="text-xl font-bold">Thông tin người đặt</label>
                <Row gutter={[12, 12]} className="mt-2">
                  <Col span={24}>
                    <div className="flex gap-2">
                      <div className="text-2l font-bold">Tên người đặt:</div>
                      <div>{data?.client?.name}</div>
                    </div>
                  </Col>
                  <Col span={24}>
                    <div className="flex gap-2">
                      <div className="text-2l font-bold">Số điện thoại:</div>
                      <div>{data?.client?.user?.numberPhone}</div>
                    </div>
                  </Col>
                  <Col span={24}>
                    <div className="flex gap-2">
                      <div className="text-2l font-bold">Địa chỉ:</div>
                      <div>{data?.addressReceiver}</div>
                    </div>
                  </Col>
                </Row>
              </Col>
              <Col xs={24} md={12}>
                <label className="text-xl font-bold">Thông tin cửa hàng</label>
                <Row gutter={[12, 12]} className="mt-2">
                  <Col span={24}>
                    <div className="flex gap-2">
                      <div className="text-2l font-bold">Tên người đặt:</div>
                      <div>{data?.shop?.name}</div>
                    </div>
                  </Col>
                  <Col span={24}>
                    <div className="flex gap-2">
                      <div className="text-2l font-bold">Số điện thoại:</div>
                      <div>{data?.shop?.user?.numberPhone}</div>
                    </div>
                  </Col>
                  <Col span={24}>
                    <div className="flex gap-2">
                      <div className="text-2l font-bold">Địa chỉ</div>
                      <div>{data?.shop?.address}</div>
                    </div>
                  </Col>
                </Row>
              </Col>
              {data?.ship ? (
                <Col xs={24} md={12}>
                  <label className="text-xl font-bold">Thông tin Tài xế</label>
                  <Row gutter={[12, 12]} className="mt-2">
                    <Col span={24}>
                      <div className="flex gap-2">
                        <div className="text-2l font-bold">Tên tài xế:</div>
                        <div>{data?.ship?.name}</div>
                      </div>
                    </Col>
                    <Col span={24}>
                      <div className="flex gap-2">
                        <div className="text-2l font-bold">Số điện thoại:</div>
                        <div>{data?.ship?.user?.numberPhone}</div>
                      </div>
                    </Col>
                    <Col span={24}>
                      <div className="flex gap-2">
                        <div className="text-2l font-bold">Địa chỉ</div>
                        <div>{data?.ship?.address}</div>
                      </div>
                    </Col>
                  </Row>
                </Col>
              ) : (
                <Col xs={24} md={12}>
                  <label className="text-xl font-bold">
                    Không có thông tin tài xế
                  </label>
                </Col>
              )}
              {data?.transaction?.name && (
                <Col>
                  <label className="text-xl font-bold">
                    Thông tin thanh toán
                  </label>
                  <Row gutter={[12, 12]} className="mt-2">
                    <Col span={24}>
                      <div className="flex gap-2">
                        <div className="text-2l font-bold">Chủ tài khoản:</div>
                        <div>{data?.transaction?.name}</div>
                      </div>
                    </Col>
                    <Col span={24}>
                      <div className="flex gap-2">
                        <div className="text-2l font-bold">STK:</div>
                        <div>{data?.transaction?.number}</div>
                      </div>
                    </Col>

                    <Col span={24}>
                      <div className="flex gap-2">
                        <div className="text-2l font-bold">QR:</div>
                        <div>
                          <Image
                            width={100}
                            height={100}
                            src={data?.transaction?.qr}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Col>
              )}
              <Col span={24}>
                <div className="flex gap-2">
                  <div className="text-2l font-bold">Hình thức thanh toán:</div>
                  <div>{paymentMethod[data?.paymentMethod || 'Cash']}</div>
                </div>
              </Col>
              <Col span={24}>
                <label className="text-xl font-bold">
                  Thông tin món ăn đã đặt
                </label>
                <Table
                  className="mt-2"
                  columns={columns}
                  dataSource={addKeyData(data?.menu)}
                  pagination={false}
                />
                <div className="flex flex-col items-end gap-1 mt-2">
                  <div className="flex gap-2">
                    <div className="text-2l font-bold">Tổng tiền món ăn</div>
                    <div>
                      {formatNumberToString(data?.foodFee.toFixed(0) || 0)} VND
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="text-2l font-bold">Phí ship</div>
                    <div>
                      {formatNumberToString(data?.shipFee.toFixed(0) || 0)} VND
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="text-2l font-bold">Phụ phí</div>
                    <div>
                      {formatNumberToString(data?.subFee.toFixed(0) || 0)} VND
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="text-2l font-bold">Tổng tiền phải trả</div>
                    <div>
                      {sumTotal(data?.subFee, data?.shipFee, data?.foodFee)} VND
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
          <Modal
            open={isOpen}
            title={
              <div className="flex items-center gap-2 text-red-600 font-bold text-lg">
                <CloseCircleOutlined />
                <span>Xác nhận hủy đơn hàng #{data?.code || ''}</span>
              </div>
            }
            onCancel={() => {
              setIsOpen(false);
            }}
            okText="Xác nhận hủy đơn"
            okButtonProps={{danger: true, loading: isLoading}}
            cancelText="Đóng"
            onOk={async () => {
              const reasonToSubmit = reason.trim();
              if (!reasonToSubmit) {
                message.warning('Vui lòng nhập hoặc chọn lý do hủy đơn hàng');
                return;
              }
              try {
                await mutateAsync({id: data?._id, reason: reasonToSubmit});
                message.success('Hủy đơn hàng thành công');
                await refetch();
                setIsOpen(false);
              } catch (error: any) {
                if (error?.data?.message) {
                  messageApi.open({
                    type: 'error',
                    content: error?.data?.message,
                  });
                } else {
                  message.error('Hủy đơn hàng thất bại');
                }
              }
            }}>
            <div className="flex flex-col gap-3 py-2">
              <p className="text-gray-600 text-sm">
                Bạn đang thao tác hủy đơn hàng <strong>#{data?.code}</strong>.
                Vui lòng nhập hoặc chọn lý do hủy:
              </p>
              <Input.TextArea
                value={reason}
                onChange={e => {
                  setReason(e.target.value);
                }}
                autoSize={{minRows: 3, maxRows: 6}}
                placeholder="Nhập lý do hủy đơn hàng..."
              />
              <div>
                <div className="text-xs text-gray-500 mb-1.5 font-medium">Gợi ý lý do nhanh:</div>
                <div className="flex flex-wrap gap-1.5">
                  {QUICK_CANCEL_REASONS.map(r => (
                    <Tag
                      key={r}
                      className="cursor-pointer hover:border-red-400 select-none py-1 text-xs"
                      color={reason === r ? 'red' : 'default'}
                      onClick={() => setReason(r)}>
                      {r}
                    </Tag>
                  ))}
                </div>
              </div>
            </div>
          </Modal>
        </div>
      </Skeleton>
    </div>
  );
};
